/**
 * Structural Opening CSG helpers.
 *
 * Turns 'Structural Openings' void elements into world-space THREE meshes so
 * host renderers (walls, floors, framing members) can subtract them, mirroring
 * BIM's hosted-cut behavior for the Structure tab > Opening panel.
 */

import * as THREE from 'three';
import { Element, StructuralOpeningGeometryData } from '../core/types';
import {
    WallFrameContext,
    shapeFromPolygon2D,
    wallBasisMatrix,
    polygonBounds2D,
    clipPolygonMaxV,
} from './wallProfileGeometry';

/** True for opening elements that cut the given host. */
export function isOpeningForHost(element: Element, hostId: string): boolean {
    if (element.category !== 'Structural Openings') return false;
    const geom = element.geometryData as StructuralOpeningGeometryData | undefined;
    return !!geom && geom.hostId === hostId;
}

/**
 * Build a world-space box Mesh for an opening geometry, ready for CSG
 * subtraction against its host. Uses an oversized local box and bakes the
 * transform into the geometry (WallRenderer's CSG pipeline consumes the raw
 * geometry only).
 *
 * 'Profile' openings (free-form void sketched on a wall face) additionally
 * need the host wall frame to place their (u, v) polygon prism; callers
 * without a wall context fall back to the bounding-box cutter so renderers
 * for other host categories stay safe.
 */
export function buildOpeningCutter(geom: StructuralOpeningGeometryData, wallContext?: WallFrameContext): THREE.Mesh | null {
    const w = geom.width;
    const h = geom.height;
    const d = geom.depth;
    if (!(w > 0) || !(h > 0) || !(d > 0)) return null;

    // Free-form sketched void: prism of the (u, v) polygon swept across the
    // host wall thickness, placed through the wall's local frame. The prism
    // overshoots the thickness like the box cutters so the CSG cut is clean.
    if (geom.kind === 'Profile' && wallContext && Array.isArray(geom.profile) && geom.profile.length >= 3) {
        let points = geom.profile as [number, number][];
        if (wallContext.cutMaxV !== undefined) {
            points = clipPolygonMaxV(points, wallContext.cutMaxV);
            if (points.length < 3) return null;
        }
        const shape = shapeFromPolygon2D(points);
        const geometry = new THREE.ExtrudeGeometry(shape, { depth: d + 0.5, bevelEnabled: false });
        geometry.translate(0, 0, -0.25); // center the overshoot on the wall mid-plane
        geometry.applyMatrix4(wallBasisMatrix(wallContext));
        const mesh = new THREE.Mesh(geometry);
        mesh.updateMatrix();
        return mesh;
    }

    // kind-specific orientation: Wall opens are rotated to the wall axis, with
    // width along the run and height vertical; vertical/shaft pierce along Z.
    let geometry: THREE.BufferGeometry;
    const rotationRad = THREE.MathUtils.degToRad(geom.rotation ?? 0);

    switch (geom.kind) {
        case 'Wall':
            // width = along-wall, height = vertical, depth = across-wall pierce
            geometry = new THREE.BoxGeometry(w, d, h);
            break;
        case 'Vertical':
        case 'Shaft':
            // footprint in XY, pierce along Z
            geometry = new THREE.BoxGeometry(w, h, d);
            break;
        case 'Profile':
        case 'ByFace':
        case 'Cut':
        default:
            geometry = new THREE.BoxGeometry(w, h, d);
            break;
    }

    const mesh = new THREE.Mesh(geometry);
    const matrix = new THREE.Matrix4().makeRotationZ(rotationRad);
    matrix.setPosition(geom.center[0], geom.center[1], geom.center[2]);
    geometry.applyMatrix4(matrix);
    mesh.updateMatrix();
    return mesh;
}

/** Bounding box of a sketched 'Profile' opening in its wall-local frame. */
export function profileOpeningBounds(profile: [number, number][]) {
    return polygonBounds2D(profile);
}
